"use client"

import {
  createContext,
  createElement,
  forwardRef,
  use,
  type ForwardRefExoticComponent,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
} from "react"
import {
  Activity,
  ALargeSmall,
  AlignLeft,
  Archive,
  ArrowDown,
  ArrowDownWideNarrow,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  ArrowUpRight,
  ArrowUpWideNarrow,
  AudioLines,
  Award,
  BadgeCheck,
  Bell,
  Blocks,
  Bold,
  BookDashed,
  Bookmark,
  BookOpen,
  Bot,
  BotMessageSquare,
  Brain,
  BriefcaseBusiness,
  Building2,
  Calendar,
  CalendarDays,
  CalendarSync,
  ChartArea,
  ChartBar,
  ChartColumnIncreasing,
  ChartNoAxesCombined,
  ChartPie,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleArrowUp,
  CircleCheck,
  CircleDashed,
  CircleHelp,
  CircleMinus,
  CirclePlay,
  CircleSlash,
  CircleStop,
  CircleUser,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  ClipboardPlus,
  Clock,
  Cloud,
  CloudUpload,
  Code,
  Compass,
  Copy,
  CornerDownRight,
  Cpu,
  CreditCard,
  Database,
  Donut,
  Download,
  Ellipsis,
  EllipsisVertical,
  Eye,
  EyeOff,
  File,
  FileArchive,
  FileInput,
  FileLock,
  FileSpreadsheet,
  FileText,
  FileUp,
  FileX,
  Filter,
  Flag,
  FlaskConical,
  FolderOpen,
  FolderPlus,
  Gauge,
  Gem,
  Gift,
  GitBranch,
  Globe,
  GraduationCap,
  Grid2X2,
  GripVertical,
  Handshake,
  Hash,
  Heading1,
  Heading2,
  Heading3,
  Headphones,
  Heart,
  History,
  IdCard,
  Image,
  ImageOff,
  Inbox,
  IndentDecrease,
  IndentIncrease,
  Info,
  Italic,
  Kanban,
  Key,
  Layers,
  LayoutTemplate,
  Leaf,
  LibraryBig,
  Lightbulb,
  Link,
  List,
  ListOrdered,
  ListTree,
  LoaderCircle,
  Lock,
  LogOut,
  Icon as LucideNodeIcon,
  Mail,
  MapPin,
  Maximize2,
  Megaphone,
  MessageCircle,
  MessageCircleMore,
  MessageCircleOff,
  MessageCirclePlus,
  MessageSquareMore,
  MessageSquareShare,
  Mic,
  Minimize2,
  Minus,
  Monitor,
  Moon,
  Navigation,
  Network,
  Newspaper,
  Palette,
  PanelBottom,
  PanelLeft,
  PanelLeftClose,
  PanelRight,
  PanelRightClose,
  Paperclip,
  Pause,
  Pencil,
  PenLine,
  PictureInPicture2,
  Pin,
  PinOff,
  Play,
  Plug,
  PlugZap,
  Plus,
  Presentation,
  Puzzle,
  Quote,
  Redo2,
  RefreshCw,
  RefreshCwOff,
  RemoveFormatting,
  Repeat,
  Reply,
  Rocket,
  RotateCcw,
  Save,
  Search,
  SeparatorHorizontal,
  Settings,
  Shapes,
  Share2,
  Shield,
  ShieldUser,
  ShoppingBag,
  SlidersHorizontal,
  SmilePlus,
  Sparkles,
  Square,
  SquareArrowOutUpRight,
  SquarePen,
  SquareSplitHorizontal,
  SquareUser,
  Star,
  Store,
  Strikethrough,
  Sun,
  SunMoon,
  Table,
  Tag,
  Tags,
  Target,
  TextAlignCenter,
  TextAlignEnd,
  TextCursorInput,
  ThumbsDown,
  ThumbsUp,
  Timer,
  Trash2,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Trophy,
  Type,
  Undo2,
  Upload,
  User,
  UserCheck,
  UserMinus,
  UserPlus,
  UserRound,
  UserRoundCheck,
  UserRoundCog,
  UserRoundX,
  Users,
  UsersRound,
  Wallet,
  Workflow,
  Wrench,
  X,
  Zap,
  ZoomIn,
  ZoomOut,
  type IconNode,
  type LucideIcon,
  type LucideProps,
} from "lucide-react"
import { twMerge } from "tailwind-merge"

type SemanticIconWeight =
  | "thin"
  | "light"
  | "regular"
  | "bold"
  | "fill"
  | "duotone"
export type SemanticIconProps = Omit<LucideProps, "ref"> & {
  /**
   * Compatibility with Phosphor call sites. Lucide does not have icon weights,
   * so this is accepted but intentionally not rendered.
   */
  weight?: SemanticIconWeight
}
type SemanticIcon = ForwardRefExoticComponent<
  SemanticIconProps & RefAttributes<SVGSVGElement>
> & {
  lucideSourceName: string
}
type SemanticIconElement = ReactElement<SemanticIconProps>
export type SemanticIconSource =
  | SemanticIcon
  | SemanticIconElement
  | string
  | null
  | undefined

interface SemanticIconProviderProps {
  children: ReactNode
  size?: number
  color?: string
  strokeWidth?: number
  absoluteStrokeWidth?: LucideProps["absoluteStrokeWidth"]
  className?: string
}

type LucideProviderElementProps = Omit<SemanticIconProviderProps, "children">
const SemanticIconContext = createContext<LucideProviderElementProps | null>(
  null
)

export function SemanticIconProvider({
  size = 16,
  children,
  ...props
}: SemanticIconProviderProps) {
  return createElement(
    SemanticIconContext.Provider,
    { value: { ...props, size } },
    children
  )
}

function createSemanticIcon(
  Icon: LucideIcon,
  displayName: string
): SemanticIcon {
  const lucideSourceName =
    (Icon as { displayName?: string }).displayName ?? displayName
  const ComponentWithRef = forwardRef<SVGSVGElement, SemanticIconProps>(
    function SemanticLucideIcon(
      { size, weight: _weight, className, ...props },
      ref
    ) {
      const context = use(SemanticIconContext)
      const {
        className: contextClassName,
        size: contextSize,
        ...contextProps
      } = context ?? {}

      return createElement(Icon, {
        ...contextProps,
        ...props,
        ref,
        className: twMerge(contextClassName, className),
        size: size ?? contextSize ?? 16,
      })
    }
  )
  const Component = Object.assign(ComponentWithRef, { lucideSourceName })
  Component.displayName = displayName

  return Component
}

export const AddIcon = createSemanticIcon(Plus, "AddIcon")
export const AddReactionIcon = createSemanticIcon(SmilePlus, "AddReactionIcon")
export const ApprovalsIcon = createSemanticIcon(BadgeCheck, "ApprovalsIcon")
export const ArchiveIcon = createSemanticIcon(Archive, "ArchiveIcon")
export const AudioFileIcon = createSemanticIcon(AudioLines, "AudioFileIcon")
export const PlanUpgradeIcon = createSemanticIcon(
  CircleArrowUp,
  "PlanUpgradeIcon"
)
export const RefreshIcon = createSemanticIcon(RefreshCw, "RefreshIcon")
export const ResetIcon = createSemanticIcon(RotateCcw, "ResetIcon")
export const ArrowDownIcon = createSemanticIcon(ArrowDown, "ArrowDownIcon")
export const DownloadIcon = createSemanticIcon(Download, "DownloadIcon")
export const ImportIcon = createSemanticIcon(FileInput, "ImportIcon")
export const BackIcon = createSemanticIcon(ArrowLeft, "BackIcon")
export const MaximizeIcon = createSemanticIcon(Maximize2, "MaximizeIcon")
export const MinimizeIcon = createSemanticIcon(Minimize2, "MinimizeIcon")
export const RedoIcon = createSemanticIcon(Redo2, "RedoIcon")
export const RepeatIcon = createSemanticIcon(Repeat, "RepeatIcon")
export const ReplyIcon = createSemanticIcon(Reply, "ReplyIcon")
export const FollowUpIcon = createSemanticIcon(CornerDownRight, "FollowUpIcon")
export const ForwardIcon = createSemanticIcon(ArrowRight, "ForwardIcon")
export const SortDescendingIcon = createSemanticIcon(
  ArrowDownWideNarrow,
  "SortDescendingIcon"
)
export const SortAscendingIcon = createSemanticIcon(
  ArrowUpWideNarrow,
  "SortAscendingIcon"
)
export const SwapIcon = createSemanticIcon(ArrowLeftRight, "SwapIcon")
export const SyncIcon = createSemanticIcon(RefreshCw, "SyncIcon")
export const SyncOffIcon = createSemanticIcon(RefreshCwOff, "SyncOffIcon")
export const TrendUpIcon = createSemanticIcon(TrendingUp, "TrendUpIcon")
export const TrendDownIcon = createSemanticIcon(TrendingDown, "TrendDownIcon")
export const UndoIcon = createSemanticIcon(Undo2, "UndoIcon")
export const ArrowUpIcon = createSemanticIcon(ArrowUp, "ArrowUpIcon")
export const OpenExternalIcon = createSemanticIcon(
  ArrowUpRight,
  "OpenExternalIcon"
)
export const UploadIcon = createSemanticIcon(Upload, "UploadIcon")
export const CloudUploadIcon = createSemanticIcon(
  CloudUpload,
  "CloudUploadIcon"
)
export const AttachmentIcon = createSemanticIcon(Paperclip, "AttachmentIcon")
export const BoardIcon = createSemanticIcon(Kanban, "BoardIcon")
export const BoardSplitIcon = createSemanticIcon(
  SquareSplitHorizontal,
  "BoardSplitIcon"
)
export const BookmarkIcon = createSemanticIcon(Bookmark, "BookmarkIcon")
export const BotIcon = createSemanticIcon(Bot, "BotIcon")
export const BrainIcon = createSemanticIcon(Brain, "BrainIcon")
export const BranchIcon = createSemanticIcon(GitBranch, "BranchIcon")
export const WorkIcon = createSemanticIcon(BriefcaseBusiness, "WorkIcon")
export const CompanyIcon = createSemanticIcon(Building2, "CompanyIcon")
// The workspace's own details — name, domain, join policy. Distinct from
// CompanyIcon, which marks the Workspace area as a whole.
export const WorkspaceProfileIcon = createSemanticIcon(
  IdCard,
  "WorkspaceProfileIcon"
)
export const DateIcon = createSemanticIcon(Calendar, "DateIcon")
export const CheckinCycleIcon = createSemanticIcon(
  CalendarSync,
  "CheckinCycleIcon"
)
export const ScheduledDateIcon = createSemanticIcon(
  CalendarDays,
  "ScheduledDateIcon"
)
export const DisclosureDownIcon = createSemanticIcon(
  ChevronDown,
  "DisclosureDownIcon"
)
export const DisclosureRightIcon = createSemanticIcon(
  ChevronRight,
  "DisclosureRightIcon"
)
export const ChatIcon = createSemanticIcon(MessageCircle, "ChatIcon")
export const MicIcon = createSemanticIcon(Mic, "MicIcon")
export const NewChatIcon = createSemanticIcon(MessageCirclePlus, "NewChatIcon")
export const ChatsIcon = createSemanticIcon(MessageCircleMore, "ChatsIcon")
export const AiChatIcon = createSemanticIcon(BotMessageSquare, "AiChatIcon")
export const CheckIcon = createSemanticIcon(Check, "CheckIcon")
export const SuccessIcon = createSemanticIcon(CircleCheck, "SuccessIcon")
export const ChevronDownIcon = createSemanticIcon(
  ChevronDown,
  "ChevronDownIcon"
)
export const ChevronLeftIcon = createSemanticIcon(
  ChevronLeft,
  "ChevronLeftIcon"
)
export const ChevronRightIcon = createSemanticIcon(
  ChevronRight,
  "ChevronRightIcon"
)
export const ChevronUpIcon = createSemanticIcon(ChevronUp, "ChevronUpIcon")
export const ChevronUpDownIcon = createSemanticIcon(
  ChevronsUpDown,
  "ChevronUpDownIcon"
)
export const CircleIcon = createSemanticIcon(Circle, "CircleIcon")
export const HintIcon = createSemanticIcon(CircleDashed, "HintIcon")
export const ClipboardCheckIcon = createSemanticIcon(
  ClipboardCheck,
  "ClipboardCheckIcon"
)
export const TaskClipboardIcon = createSemanticIcon(
  ClipboardList,
  "TaskClipboardIcon"
)
export const AddTaskIcon = createSemanticIcon(ClipboardPlus, "AddTaskIcon")
export const TimeIcon = createSemanticIcon(Clock, "TimeIcon")
export const RecentDownloadIcon = createSemanticIcon(
  History,
  "RecentDownloadIcon"
)
export const CodeIcon = createSemanticIcon(Code, "CodeIcon")
export const CollaborationPlatformIcon = createSemanticIcon(
  MessageSquareShare,
  "CollaborationPlatformIcon"
)
export const CommentIcon = createSemanticIcon(MessageSquareMore, "CommentIcon")
export const CommentsDisabledIcon = createSemanticIcon(
  MessageCircleOff,
  "CommentsDisabledIcon"
)
export const ComposeIcon = createSemanticIcon(SquarePen, "ComposeIcon")
export const ConnectedIcon = createSemanticIcon(PlugZap, "ConnectedIcon")
export const ContentViewIcon = createSemanticIcon(Newspaper, "ContentViewIcon")
// The shape a document is poured into, as opposed to the document itself.
export const DocumentLayoutIcon = createSemanticIcon(
  LayoutTemplate,
  "DocumentLayoutIcon"
)
export const CopyIcon = createSemanticIcon(Copy, "CopyIcon")
export const AreaChartIcon = createSemanticIcon(ChartArea, "AreaChartIcon")
export const BarChartIcon = createSemanticIcon(ChartBar, "BarChartIcon")
export const PieChartIcon = createSemanticIcon(ChartPie, "PieChartIcon")
export const TrendingDataIcon = createSemanticIcon(
  ChartNoAxesCombined,
  "TrendingDataIcon"
)
export const UsageIcon = createSemanticIcon(Donut, "UsageIcon")
export const DeleteIcon = createSemanticIcon(Trash2, "DeleteIcon")
export const DeleteDismissIcon = createSemanticIcon(Trash2, "DeleteDismissIcon")
export const PremiumTierIcon = createSemanticIcon(Gem, "PremiumTierIcon")
export const CloseIcon = createSemanticIcon(X, "CloseIcon")
export const CloseCircleIcon = createSemanticIcon(CircleX, "CloseCircleIcon")
export const RemoveCircleIcon = createSemanticIcon(
  CircleMinus,
  "RemoveCircleIcon"
)
export const CompassIcon = createSemanticIcon(Compass, "CompassIcon")
export const DocumentIcon = createSemanticIcon(File, "DocumentIcon")
export const DocumentUploadIcon = createSemanticIcon(
  FileUp,
  "DocumentUploadIcon"
)
export const DocumentUnavailableIcon = createSemanticIcon(
  FileX,
  "DocumentUnavailableIcon"
)
export const DocumentLockedIcon = createSemanticIcon(
  FileLock,
  "DocumentLockedIcon"
)
export const PdfDocumentIcon = createSemanticIcon(FileText, "PdfDocumentIcon")
export const TextDocumentIcon = createSemanticIcon(FileText, "TextDocumentIcon")
export const DocumentListIcon = createSemanticIcon(FileText, "DocumentListIcon")
export const DataDocumentIcon = createSemanticIcon(
  FileSpreadsheet,
  "DataDocumentIcon"
)
export const DocumentErrorIcon = createSemanticIcon(FileX, "DocumentErrorIcon")
export const TemplateIcon = createSemanticIcon(BookDashed, "TemplateIcon")
export const ZipFileIcon = createSemanticIcon(FileArchive, "ZipFileIcon")
export const EditIcon = createSemanticIcon(Pencil, "EditIcon")
export const ErrorIcon = createSemanticIcon(CircleAlert, "ErrorIcon")
export const ViewIcon = createSemanticIcon(Eye, "ViewIcon")
export const HiddenIcon = createSemanticIcon(EyeOff, "HiddenIcon")
export const FilterIcon = createSemanticIcon(Filter, "FilterIcon")
export const FastIcon = createSemanticIcon(Zap, "FastIcon")
export const FlagIcon = createSemanticIcon(Flag, "FlagIcon")
export const FlowIcon = createSemanticIcon(Workflow, "FlowIcon")
export const OpenFolderIcon = createSemanticIcon(FolderOpen, "OpenFolderIcon")
export const AddFolderIcon = createSemanticIcon(FolderPlus, "AddFolderIcon")
export const GiftIcon = createSemanticIcon(Gift, "GiftIcon")
export const GlobalIcon = createSemanticIcon(Globe, "GlobalIcon")
export const GridIcon = createSemanticIcon(Grid2X2, "GridIcon")
export const GuestIcon = createSemanticIcon(User, "GuestIcon")
export const HashIcon = createSemanticIcon(Hash, "HashIcon")
export const HistoryIcon = createSemanticIcon(History, "HistoryIcon")
export const ImageIcon = createSemanticIcon(Image, "ImageIcon")
export const ImageUnavailableIcon = createSemanticIcon(
  ImageOff,
  "ImageUnavailableIcon"
)
export const AiImageIcon = createSemanticIcon(Image, "AiImageIcon")
export const InfoIcon = createSemanticIcon(Info, "InfoIcon")
export const KeyIcon = createSemanticIcon(Key, "KeyIcon")
export const LayerIcon = createSemanticIcon(Layers, "LayerIcon")
export const LibraryIcon = createSemanticIcon(LibraryBig, "LibraryIcon")
export const IdeaIcon = createSemanticIcon(Lightbulb, "IdeaIcon")
export const DividerIcon = createSemanticIcon(
  SeparatorHorizontal,
  "DividerIcon"
)
export const LinkIcon = createSemanticIcon(Link, "LinkIcon")
export const ListIcon = createSemanticIcon(List, "ListIcon")
export const TreeListIcon = createSemanticIcon(ListTree, "TreeListIcon")
export const IndentIcon = createSemanticIcon(IndentIncrease, "IndentIcon")
export const OutdentIcon = createSemanticIcon(IndentDecrease, "OutdentIcon")
export const LocationIcon = createSemanticIcon(MapPin, "LocationIcon")
export const LockedIcon = createSemanticIcon(Lock, "LockedIcon")
export const EmailIcon = createSemanticIcon(Mail, "EmailIcon")
export const InboxIcon = createSemanticIcon(Inbox, "InboxIcon")
export const InboxImportIcon = createSemanticIcon(Inbox, "InboxImportIcon")
export const MoreIcon = createSemanticIcon(Ellipsis, "MoreIcon")
export const MoreActionsIcon = createSemanticIcon(Ellipsis, "MoreActionsIcon")
export const MoreActionsVerticalIcon = createSemanticIcon(
  EllipsisVertical,
  "MoreActionsVerticalIcon"
)
export const NavigationIcon = createSemanticIcon(Navigation, "NavigationIcon")
export const OpenIcon = createSemanticIcon(SquareArrowOutUpRight, "OpenIcon")
export const OrganizationIcon = createSemanticIcon(Network, "OrganizationIcon")
export const CollapseLeftPanelIcon = createSemanticIcon(
  PanelLeftClose,
  "CollapseLeftPanelIcon"
)
export const LeftPanelIcon = createSemanticIcon(PanelLeft, "LeftPanelIcon")
export const CollapseRightPanelIcon = createSemanticIcon(
  PanelRightClose,
  "CollapseRightPanelIcon"
)
export const RightPanelIcon = createSemanticIcon(PanelRight, "RightPanelIcon")
/** The ask bar docked along the bottom edge -- chat's smallest resting size. */
export const BottomPanelIcon = createSemanticIcon(
  PanelBottom,
  "BottomPanelIcon"
)
/** The floating chat card: a small window layered over the page behind it. */
export const FloatingChatIcon = createSemanticIcon(
  PictureInPicture2,
  "FloatingChatIcon"
)
export const PauseIcon = createSemanticIcon(Pause, "PauseIcon")
export const PaymentIcon = createSemanticIcon(CreditCard, "PaymentIcon")
export const WriteIcon = createSemanticIcon(PenLine, "WriteIcon")
export const PeopleIcon = createSemanticIcon(UserRound, "PeopleIcon")
export const InvitePeopleIcon = createSemanticIcon(UserPlus, "InvitePeopleIcon")
export const PeopleBlockedIcon = createSemanticIcon(
  UserMinus,
  "PeopleBlockedIcon"
)
export const TeamIcon = createSemanticIcon(UsersRound, "TeamIcon")
export const AddTeamIcon = createSemanticIcon(UserPlus, "AddTeamIcon")
export const UserIcon = createSemanticIcon(User, "UserIcon")
export const AddUserIcon = createSemanticIcon(UserPlus, "AddUserIcon")
export const DelegateUserIcon = createSemanticIcon(
  UserRoundCog,
  "DelegateUserIcon"
)
export const ContributorUserIcon = createSemanticIcon(
  Handshake,
  "ContributorUserIcon"
)
export const WatcherUserIcon = createSemanticIcon(Eye, "WatcherUserIcon")
export const AvailableUserIcon = createSemanticIcon(
  UserCheck,
  "AvailableUserIcon"
)
export const WorkUserIcon = createSemanticIcon(SquareUser, "WorkUserIcon")
export const UserCircleIcon = createSemanticIcon(CircleUser, "UserCircleIcon")
export const TeamOwnerIcon = createSemanticIcon(UserRoundCheck, "TeamOwnerIcon")
export const PaletteIcon = createSemanticIcon(Palette, "PaletteIcon")
export const BlockedUserIcon = createSemanticIcon(UserMinus, "BlockedUserIcon")
export const DeactivatedUserIcon = createSemanticIcon(
  UserRoundX,
  "DeactivatedUserIcon"
)
export const SwapUserIcon = createSemanticIcon(UserRoundCog, "SwapUserIcon")
export const PinIcon = createSemanticIcon(Pin, "PinIcon")
export const UnpinIcon = createSemanticIcon(PinOff, "UnpinIcon")
export const PlayIcon = createSemanticIcon(Play, "PlayIcon")
export const PlayCircleIcon = createSemanticIcon(CirclePlay, "PlayCircleIcon")
export const ConnectorIcon = createSemanticIcon(Blocks, "ConnectorIcon")

export const IntegrationConnectedIcon = createSemanticIcon(
  PlugZap,
  "IntegrationConnectedIcon"
)
export const IntegrationDisconnectedIcon = createSemanticIcon(
  Plug,
  "IntegrationDisconnectedIcon"
)
export const HelpIcon = createSemanticIcon(CircleHelp, "HelpIcon")
export const DragHandleIcon = createSemanticIcon(GripVertical, "DragHandleIcon")
export const StopRecordingIcon = createSemanticIcon(
  CircleStop,
  "StopRecordingIcon"
)
export const LaunchIcon = createSemanticIcon(Rocket, "LaunchIcon")
export const SaveIcon = createSemanticIcon(Save, "SaveIcon")
export const SearchIcon = createSemanticIcon(Search, "SearchIcon")
export const AiSearchIcon = createSemanticIcon(Search, "AiSearchIcon")
export const SettingsIcon = createSemanticIcon(Settings, "SettingsIcon")
export const DisconnectIcon = createSemanticIcon(CircleSlash, "DisconnectIcon")
export const ThemeIcon = createSemanticIcon(SunMoon, "ThemeIcon")
export const ThemeLightIcon = createSemanticIcon(Sun, "ThemeLightIcon")
export const ThemeDarkIcon = createSemanticIcon(Moon, "ThemeDarkIcon")
export const ThemeSystemIcon = createSemanticIcon(Monitor, "ThemeSystemIcon")
export const NotificationIcon = createSemanticIcon(Bell, "NotificationIcon")
export const CustomizationIcon = createSemanticIcon(
  SlidersHorizontal,
  "CustomizationIcon"
)
export const StoreIcon = createSemanticIcon(Store, "StoreIcon")
export const ObjectTypeIcon = createSemanticIcon(Shapes, "ObjectTypeIcon")
export const ShareIcon = createSemanticIcon(Share2, "ShareIcon")
export const SecurityIcon = createSemanticIcon(Shield, "SecurityIcon")
export const UserSecurityIcon = createSemanticIcon(
  ShieldUser,
  "UserSecurityIcon"
)
export const SignOutIcon = createSemanticIcon(LogOut, "SignOutIcon")
export const SkillIcon = createSemanticIcon(Award, "SkillIcon")
export const SlideIcon = createSemanticIcon(Presentation, "SlideIcon")
export const EditSlideIcon = createSemanticIcon(
  ChartColumnIncreasing,
  "EditSlideIcon"
)
export const AiIcon = createSemanticIcon(Sparkles, "AiIcon")
export const LoadingIcon = createSemanticIcon(LoaderCircle, "LoadingIcon")
export const FavoriteIcon = createSemanticIcon(Star, "FavoriteIcon")
export const StatusIcon = createSemanticIcon(Activity, "StatusIcon")
export const StopIcon = createSemanticIcon(Square, "StopIcon")
export const TableIcon = createSemanticIcon(Table, "TableIcon")
export const DeleteTableIcon = createSemanticIcon(Table, "DeleteTableIcon")
export const TagIcon = createSemanticIcon(Tag, "TagIcon")
export const TagsIcon = createSemanticIcon(Tags, "TagsIcon")
export const TargetIcon = createSemanticIcon(Target, "TargetIcon")
export const GoalIcon = createSemanticIcon(Target, "GoalIcon")
export const TextIcon = createSemanticIcon(Type, "TextIcon")
export const TextAlignCenterIcon = createSemanticIcon(
  TextAlignCenter,
  "TextAlignCenterIcon"
)
export const TextAlignLeftIcon = createSemanticIcon(
  AlignLeft,
  "TextAlignLeftIcon"
)
export const TextAlignRightIcon = createSemanticIcon(
  TextAlignEnd,
  "TextAlignRightIcon"
)
export const BulletedListIcon = createSemanticIcon(List, "BulletedListIcon")
export const DescriptionIcon = createSemanticIcon(FileText, "DescriptionIcon")
export const TextStyleIcon = createSemanticIcon(ALargeSmall, "TextStyleIcon")
export const TextFieldIcon = createSemanticIcon(
  TextCursorInput,
  "TextFieldIcon"
)
export const BoldIcon = createSemanticIcon(Bold, "BoldIcon")
export const HeadingOneIcon = createSemanticIcon(Heading1, "HeadingOneIcon")
export const HeadingTwoIcon = createSemanticIcon(Heading2, "HeadingTwoIcon")
export const HeadingThreeIcon = createSemanticIcon(Heading3, "HeadingThreeIcon")
export const ItalicIcon = createSemanticIcon(Italic, "ItalicIcon")
export const NumberedListIcon = createSemanticIcon(
  ListOrdered,
  "NumberedListIcon"
)
export const QuoteIcon = createSemanticIcon(Quote, "QuoteIcon")
export const RemoveFormattingIcon = createSemanticIcon(
  RemoveFormatting,
  "RemoveFormattingIcon"
)
export const StrikethroughIcon = createSemanticIcon(
  Strikethrough,
  "StrikethroughIcon"
)
export const TimerIcon = createSemanticIcon(Timer, "TimerIcon")
export const SpeedIcon = createSemanticIcon(Gauge, "SpeedIcon")
export const WalletIcon = createSemanticIcon(Wallet, "WalletIcon")
export const WarningIcon = createSemanticIcon(TriangleAlert, "WarningIcon")
export const ZoomInIcon = createSemanticIcon(ZoomIn, "ZoomInIcon")
export const ZoomOutIcon = createSemanticIcon(ZoomOut, "ZoomOutIcon")
export const MinusIcon = createSemanticIcon(Minus, "MinusIcon")
export const NegativeFeedbackIcon = createSemanticIcon(
  ThumbsDown,
  "NegativeFeedbackIcon"
)
export const PositiveFeedbackIcon = createSemanticIcon(
  ThumbsUp,
  "PositiveFeedbackIcon"
)

/** Curated Lucide marks for persistent team identities. */
export const TEAM_MARK_ICONS = {
  layers: createSemanticIcon(Layers, "TeamMarkLayers"),
  code: createSemanticIcon(Code, "TeamMarkCode"),
  megaphone: createSemanticIcon(Megaphone, "TeamMarkMegaphone"),
  "chart-no-axes-combined": createSemanticIcon(
    ChartNoAxesCombined,
    "TeamMarkChartNoAxesCombined"
  ),
  globe: createSemanticIcon(Globe, "TeamMarkGlobe"),
  rocket: createSemanticIcon(Rocket, "TeamMarkRocket"),
  shield: createSemanticIcon(Shield, "TeamMarkShield"),
  users: createSemanticIcon(Users, "TeamMarkUsers"),
  lightbulb: createSemanticIcon(Lightbulb, "TeamMarkLightbulb"),
  compass: createSemanticIcon(Compass, "TeamMarkCompass"),
  heart: createSemanticIcon(Heart, "TeamMarkHeart"),
  headphones: createSemanticIcon(Headphones, "TeamMarkHeadphones"),
  palette: createSemanticIcon(Palette, "TeamMarkPalette"),
  "flask-conical": createSemanticIcon(FlaskConical, "TeamMarkFlaskConical"),
  "briefcase-business": createSemanticIcon(
    BriefcaseBusiness,
    "TeamMarkBriefcaseBusiness"
  ),
  "building-2": createSemanticIcon(Building2, "TeamMarkBuilding2"),
  cpu: createSemanticIcon(Cpu, "TeamMarkCpu"),
  database: createSemanticIcon(Database, "TeamMarkDatabase"),
  cloud: createSemanticIcon(Cloud, "TeamMarkCloud"),
  wrench: createSemanticIcon(Wrench, "TeamMarkWrench"),
  zap: createSemanticIcon(Zap, "TeamMarkZap"),
  gem: createSemanticIcon(Gem, "TeamMarkGem"),
  flag: createSemanticIcon(Flag, "TeamMarkFlag"),
  trophy: createSemanticIcon(Trophy, "TeamMarkTrophy"),
  "book-open": createSemanticIcon(BookOpen, "TeamMarkBookOpen"),
  "graduation-cap": createSemanticIcon(GraduationCap, "TeamMarkGraduationCap"),
  puzzle: createSemanticIcon(Puzzle, "TeamMarkPuzzle"),
  network: createSemanticIcon(Network, "TeamMarkNetwork"),
  leaf: createSemanticIcon(Leaf, "TeamMarkLeaf"),
  "shopping-bag": createSemanticIcon(ShoppingBag, "TeamMarkShoppingBag"),
  wallet: createSemanticIcon(Wallet, "TeamMarkWallet"),
  sparkles: createSemanticIcon(Sparkles, "TeamMarkSparkles"),
} as const

// A separate chunk keeps the full library out of ordinary team views.
export type TeamIconCatalog = Record<string, IconNode>
let teamIconCatalogPromise: Promise<TeamIconCatalog> | undefined
export function loadTeamIconCatalog(): Promise<TeamIconCatalog> {
  teamIconCatalogPromise ??= import("@/config/lucide-team-icons.json")
    .then((module) => module.default as unknown as TeamIconCatalog)
    .catch((error: unknown) => {
      teamIconCatalogPromise = undefined
      // eslint-disable-next-line no-console -- Report optional chunk failures once before callers recover with initials or retry UI.
      console.error("Couldn’t load the Lucide team icon catalog", error)
      throw error
    })
  return teamIconCatalogPromise
}
export function CatalogTeamIcon({
  catalog,
  name,
  ...props
}: { catalog: TeamIconCatalog; name: string } & SemanticIconProps) {
  const context = use(SemanticIconContext)
  const nodes = Object.hasOwn(catalog, name) ? catalog[name] : undefined
  if (!nodes) return null
  const { weight: _weight, ...svgProps } = props
  return createElement(LucideNodeIcon, {
    ...context,
    ...svgProps,
    size: props.size ?? context?.size ?? 16,
    iconNode: nodes,
  })
}
